import React, { type ChangeEventHandler, useState } from 'react';
import { useField } from 'react-final-form';

import { ApolloError } from '@apollo/client';
import { CloudUploadOutlined } from '@mui/icons-material';
import { Box, Button, LinearProgress, Stack, Typography } from '@mui/material';
import { useSidebarApiClient } from '@wp-block-builder/components/SidebarApiClientContext';

import { alertError } from '@waypoint/react/notifications';

type Props = {
  name: string;
};
export default function ImageManager({ name }: Props) {
  const urlInput = useField<string | null>(`${name}.props.url`).input;
  const uploadIdInput = useField<string | null>(`${name}.props.uploadId`).input;

  const { createImageUpload, refreshImageUpload } = useSidebarApiClient();
  const [uploadingFraction, setUploadingFraction] = useState<number | null>(null);

  const changeHandler: ChangeEventHandler<HTMLInputElement> = async (ev) => {
    if (ev.target.files === null) {
      return;
    }
    if (ev.target.files.length === 0) {
      return;
    }

    setUploadingFraction(0);

    try {
      const file = ev.target.files[0];
      const { signedS3UploadUrl, waypointTemplateImageId } = await createImageUpload(file);
      setUploadingFraction(20);
      await fetch(signedS3UploadUrl, { headers: { 'Content-Type': file.type }, method: 'PUT', body: file });
      setUploadingFraction(90);
      const { imageUrl, imageId } = await refreshImageUpload(waypointTemplateImageId);
      setUploadingFraction(95);
      urlInput.onChange(imageUrl);
      uploadIdInput.onChange(imageId);
      setUploadingFraction(null);
    } catch (e) {
      if (e instanceof ApolloError) {
        alertError(e.message);
      } else {
        alertError('There was an error uploading your image');
      }
    }
  };

  const buttonBgColor = urlInput.value ? 'transparent' : 'cadet.100';

  const renderImagePreview = () => {
    return urlInput.value ? (
      <Box component="img" src={urlInput.value} sx={{ objectFit: 'scale-down', width: '100%', maxHeight: 128 }} />
    ) : (
      <Stack direction="row" spacing={1} alignItems="center">
        <CloudUploadOutlined />
        <Typography fontWeight="medium">Upload image</Typography>
      </Stack>
    );
  };

  if (typeof uploadingFraction === 'number') {
    return (
      <Box sx={{ height: 128, bgcolor: buttonBgColor }}>
        <LinearProgress variant="determinate" value={uploadingFraction} />
      </Box>
    );
  }

  return (
    <Button component="label" sx={{ height: 128, width: '100%', bgcolor: buttonBgColor }}>
      {renderImagePreview()}
      <input hidden accept=".png, .jpg, .jpeg, .gif" type="file" onChange={changeHandler} />
    </Button>
  );
}
